import { Button, Flex, Input } from "antd";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixIcon, PlusIcon, XIcon } from "@phosphor-icons/react";
import { useTranslate } from "@refinedev/core";
import { CSSProperties, useState } from "react";
import { nanoid } from "nanoid";

type BulletPointItem = { id: string; content: string };

const MIN_BULLET_POINTS = 2;
const MAX_BULLET_POINTS = 6;

const SortableBulletPointRow = ({
  id,
  content,
  onCommit,
  onRemove,
  autoFocus,
  removeDisabled,
}: {
  id: string;
  content: string;
  onCommit: (value: string) => void;
  onRemove: () => void;
  autoFocus?: boolean;
  removeDisabled?: boolean;
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Flex ref={setNodeRef} style={style} align="center" gap={4} {...attributes}>
      <span
        {...listeners}
        style={{
          cursor: "grab",
          display: "inline-flex",
          alignItems: "center",
          touchAction: "none",
        }}
      >
        <DotsSixIcon size={20} />
      </span>
      <Input.TextArea
        autoFocus={autoFocus}
        defaultValue={content}
        autoSize={{ minRows: 1, maxRows: 4 }}
        onBlur={(e) => onCommit(e.target.value)}
        onPressEnter={(e) => {
          if (e.shiftKey) return;
          e.preventDefault();
          e.currentTarget.blur();
        }}
        style={{ flex: 1 }}
      />
      <Button
        type="text"
        size="small"
        icon={<XIcon size={16} />}
        onClick={onRemove}
        disabled={removeDisabled}
      />
    </Flex>
  );
};

export const TableBulletPointsContentFeature = ({
  initialBulletPoints,
  onChange,
}: {
  initialBulletPoints: string[];
  onChange: (bulletPoints: string[]) => void;
}) => {
  const t = useTranslate();
  const [items, setItems] = useState<BulletPointItem[]>(() =>
    initialBulletPoints.map((content) => ({ id: nanoid(), content }))
  );
  const [autoFocusId, setAutoFocusId] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const commit = (next: BulletPointItem[]) => {
    setItems(next);
    onChange(next.map((item) => item.content));
  };

  const handleCommit = (id: string, value: string) => {
    const target = items.find((item) => item.id === id);
    if (!target || target.content === value) return;
    commit(
      items.map((item) =>
        item.id === id ? { ...item, content: value } : item
      )
    );
  };

  const handleRemove = (id: string) => {
    if (items.length <= MIN_BULLET_POINTS) return;
    commit(items.filter((item) => item.id !== id));
  };

  const handleAdd = () => {
    if (items.length >= MAX_BULLET_POINTS) return;
    const newItem = { id: nanoid(), content: "" };
    setAutoFocusId(newItem.id);
    setItems([...items, newItem]);
  };

  const canAdd = items.length < MAX_BULLET_POINTS;
  const canRemove = items.length > MIN_BULLET_POINTS;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    commit(arrayMove(items, oldIndex, newIndex));
  };

  return (
    <Flex vertical gap={8}>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={items.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <Flex vertical gap={8}>
            {items.map((item) => (
              <SortableBulletPointRow
                key={item.id}
                id={item.id}
                content={item.content}
                autoFocus={item.id === autoFocusId}
                onCommit={(value) => handleCommit(item.id, value)}
                onRemove={() => handleRemove(item.id)}
                removeDisabled={!canRemove}
              />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>
      <Button
        type="dashed"
        icon={<PlusIcon size={16} />}
        onClick={handleAdd}
        disabled={!canAdd}
        style={{ alignSelf: "center", marginTop: 8 }}
      >
        {t("components.templates.settings.tableTab.addBulletPoint")}
      </Button>
    </Flex>
  );
};
